<script lang="ts" setup>
/**
 * @component BaseField
 * @description 字段级渲染组件，负责将 field 配置渲染为实际的输入组件
 *
 * ## 核心职责
 *
 * 1. **组件解析**：按优先级解析要渲染的组件（teleport 插槽 > 自定义组件 > 内置组件映射 > 原始值）
 * 2. **v-model 双向绑定**：通过 computed getter/setter 将组件值与 formData 的深层路径绑定
 * 3. **valueFormatter 处理**：支持 get/set 格式化函数，用于数据转换（如日期格式化）
 * 4. **属性合并**：将注入的默认 props、组件传入的 attrs 合并为最终属性
 *
 * ## 组件解析优先级
 *
 * ```
 * teleport 组件（ProForm 插槽注入）
 *   ↓ 未匹配
 * 自定义组件（ProComponentProvider 的 componentMap）
 *   ↓ 未匹配
 * 内置组件映射（componentMap: input → Input, select → Select ...）
 *   ↓ 未匹配
 * 原始 component 值（可能是字符串或 Vue 组件对象）
 * ```
 *
 * @param {string | Component} [component] - 组件名或组件对象
 * @param {string} [path] - 字段路径，用于绑定表单数据
 *
 * @slot default - 默认插槽（透传给组件）
 */
import {
  type Component,
  computed,
  inject,
  mergeProps,
  ref,
  toValue,
  useAttrs,
} from 'vue';
import { cloneDeep } from '../../../../shared/core';
import {
  useInjectDisabled,
  useInjectFormItemContext,
} from '../../../../shared/ui';
import type { Field } from '../../types';
import { ContainerFragment, SlotComponent } from '..';
import { componentMap, InjectionFormSlotsKey } from '../../constants';
import {
  INJECT_COMPONENTS,
  getInjectConfig,
} from '../../../component-provider/constants';
import { useForm } from '../../hooks/useForm';
import { getInitProps } from './utils';
import { useLocale } from '../../../../locale';

defineOptions({ name: 'BaseField', inheritAttrs: false });

type Props = {
  component?: string | Component;
  path?: string;
};
const props = withDefaults(defineProps<Props>(), {
  component: undefined,
  path: '',
});

const { t } = useLocale();

const form = useForm(false);

const componentRef = ref<any>();

const formItemContext = useInjectFormItemContext();
const triggerFormItemChange = () => {
  formItemContext.onFieldChange();
};

const attrs: any = useAttrs();

// 全局默认配置注入：key = 组件名 + picker 修饰段；inject 须在 setup 同步期调用
const pickerVal = attrs.picker;
const injectConfigKey =
  typeof props.component === 'string'
    ? [props.component, pickerVal].filter(Boolean).join('.')
    : '';
const componentInjectConfig = injectConfigKey
  ? getInjectConfig(injectConfigKey)
  : undefined;
const rawInjectComponentProps = componentInjectConfig
  ? inject(componentInjectConfig.injectionKey, componentInjectConfig.default)
  : undefined;

// setter: 向 formData 写入值，支持 valueFormatter / valueFormatter.set 转换
const value = computed({
  get() {
    let val = form.getFormData?.(props.path);
    const { valueFormatter } = groupedAttrs.value;
    if (
      typeof valueFormatter === 'object' &&
      typeof valueFormatter.get === 'function'
    ) {
      val = valueFormatter.get(val);
    }
    return val;
  },
  set(val) {
    let newVal = val;
    const { valueFormatter } = groupedAttrs.value;
    const setFormatter =
      typeof valueFormatter === 'function'
        ? valueFormatter
        : typeof valueFormatter === 'object' &&
            typeof valueFormatter?.set === 'function'
          ? valueFormatter.set
          : undefined;

    if (setFormatter) {
      // 仅当 set 转换函数需要旧值参数时，才获取旧值上下文
      const oldVal =
        setFormatter.length > 1
          ? cloneDeep(form.getFormData?.(props.path))
          : undefined;
      newVal = setFormatter(val, oldVal);
    }
    form.setFormData?.(props.path, newVal);
    triggerFormItemChange();
  },
});

const parentDisabled = useInjectDisabled();

/*
 * 将注入的默认 props、外部传入的 attrs、field 配置合并为最终属性
 * 属性分为三类：绑定属性（v-model）、组件属性（传递给组件）、插槽（动态渲染）
 */
const groupedAttrs = computed(() => {
  const currentInjectProps = toValue(rawInjectComponentProps) || {};
  const initProps = getInitProps(
    { component: props.component, picker: attrs.picker } as Field,
    currentInjectProps,
    t
  );
  const effectiveModelProp = attrs.modelProp ?? initProps.modelProp ?? 'value';
  const mergedProps = mergeProps(
    initProps,
    attrs,
    { class: attrs.componentClass, style: attrs.componentStyle },
    { class: initProps.componentClass, style: initProps.componentStyle },
    { disabled: attrs.disabled ?? parentDisabled.value ?? initProps.disabled },
    { modelProp: effectiveModelProp }
  ) as Required<Field>;

  // prettier-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { valueFormatter, modelProp, slots, componentClass, componentStyle, componentContainer, [effectiveModelProp]: _modelVal, [`onUpdate:${effectiveModelProp}`]: _onUpdateModelVal, ...bindAttrs } = mergedProps as any;

  return {
    attrs: bindAttrs,
    slots,
    componentContainer,
    modelProp,
    valueFormatter,
  };
});

// 从 ProForm 插槽注入的 slots 集合（优先级最高）
const formSlots = inject(InjectionFormSlotsKey, undefined) as
  | Record<string, any>
  | undefined;

// 从 ProComponentProvider 注入的自定义组件映射
const customComponents = inject(INJECT_COMPONENTS, {});

/*
 * 按优先级解析最终要渲染的组件：
 * ProForm 插槽 → 自定义组件映射 → 内置组件映射 → 原始 component 值
 */
const is = computed(() => {
  const teleportSlot = props.path && formSlots?.[props.path];
  if (teleportSlot) return teleportSlot;
  if (typeof props.component === 'string') {
    const resolved =
      (customComponents as any)[props.component] ||
      (componentMap as any)[props.component];
    if (resolved) return resolved;
    console.warn(
      `[pro-components] 未识别的组件类型 "${props.component}"，已降级为原生元素渲染。如为自定义组件请在 componentMap 或 ProComponentProvider 中注册。`
    );
    return props.component;
  }
  return props.component;
});

defineExpose({
  getComponentRef: () => componentRef.value,
  getComponentComputedProps: () => groupedAttrs.value.attrs as any,
});
</script>

<template>
  <ContainerFragment
    :component="groupedAttrs.componentContainer"
    :path="props.path"
  >
    <component
      :is="is"
      v-if="is"
      v-bind="groupedAttrs.attrs"
      ref="componentRef"
      v-model:[`${groupedAttrs.modelProp}`]="value"
      :path="props.path"
    >
      <template
        v-for="(slot, name) in groupedAttrs.slots"
        :key="name"
        #[name]="scoped"
      >
        <SlotComponent :path="props.path" v-bind="scoped" :component="slot" />
      </template>
    </component>
  </ContainerFragment>
</template>
